import React, { useRef, useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { Brain } from 'lucide-react';
import {
    Select,
    MenuItem,
    Button,
    ButtonGroup,
    Box,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import { useShimmer } from './ShimmerContext';
import ShimmerHighlight from './ShimmerHighlight';
import ExportModal from './ExportModal';
import MELWizard from './MELWizard';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { useTutorial } from "./TutorialProvider";

const DataModelSelector = ({ currentModel, onModelChange, modelData, handleImportedData }) => {
    const fileInputRef = useRef(null);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const { currentUser } = useAuth();
    const isStudent = currentUser?.role === 'student';
    const { activeShimmer } = useShimmer();
    const { t } = useTranslation();
    const [showAIDialog, setShowAIDialog] = useState(false);
    const isMobile = useMediaQuery('(max-width:480px)');
    const [localModel, setLocalModel] = useState(currentModel);
    const { completeAction } = useTutorial();
    const showAdvancedControls = currentUser?.role === 'instructor' ||
        currentUser?.role === 'explorer';

    useEffect(() => {
        if (modelData && !currentModel) {
            const modelKeys = Object.keys(modelData);
            if (modelKeys.length > 0) {
                onModelChange(modelKeys[0]);
            }
        }
    }, [modelData, currentModel, onModelChange]);

    useEffect(() => {
        setLocalModel(currentModel);
    }, [currentModel]);

    const buttonStyle = {
        textTransform: 'none',
        fontWeight: 500,
        bgcolor: 'grey.100',
        color: 'grey.800',
        border: 'none',
        fontSize: isMobile ? 16 : 'inherit',
        '&:hover': {
            bgcolor: 'grey.200',
            border: 'none',
        },
        '& .MuiButton-startIcon': {
            color: 'grey.600',
        }
    };

    const handleModelChange = (event) => {
        completeAction('header.select');
        const newValue = event.target.value;
        setLocalModel(newValue);
        onModelChange(newValue);
    };

    const handleSaveStudentChanges = async () => {
        if (!currentUser) return;
        completeAction('header.savechanges');
        try {
            setSaving(true);
            const db = getFirestore();
            const modelRef = doc(db, 'studentModels', `${currentUser.uid}_${currentModel}`);

            const modelDataString = JSON.stringify(modelData[currentModel]);
            await setDoc(modelRef, {
                userId: currentUser.uid,
                modelName: currentModel,
                modelData: modelDataString,
                lastModified: new Date().toISOString()
            });

            alert(t('header.savesuccess'));
        } catch (error) {
            console.error('Error saving changes:', error);
            alert(t('header.savefailure'));
        } finally {
            setSaving(false);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const importedData = JSON.parse(e.target.result);
                handleImportedData(importedData);
                alert(t('models.importsuccess'));
            } catch (error) {
                console.error('Error parsing imported file:', error);
                alert(t('models.importfailure'));
            }
        };

        reader.readAsText(file);
    };

    const handleExport = (exportOption) => {
        let dataToExport;
        let filename;

        if (exportOption === 'all') {
            // Add plausibility to all models before export
            const exportData = {...modelData};
            Object.keys(exportData).forEach(modelKey => {
                if (exportData[modelKey] && exportData[modelKey].nodes) {
                    // Add plausibility to model nodes
                    exportData[modelKey].nodes.forEach(node => {
                        if (node.type === 'model') {
                            // Calculate plausibility from links
                            const links = exportData[modelKey].links || [];
                            node.plausibility = links.reduce((acc, link) => {
                                const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                                const targetId = typeof link.target === 'object' ? link.target.id : link.target;

                                // Check if this link connects to this model
                                if (sourceId === node.id || targetId === node.id) {
                                    // Skip NO_EFFECT_EXPLICIT (-1) links
                                    return acc + (link.value === -1 ? 0 : link.value);
                                }
                                return acc;
                            }, 0);
                        }
                    });
                }
            });

            dataToExport = exportData;
            filename = 'all_mel_models.json';
        } else {
            // Just this model
            const modelToExport = {...modelData[currentModel]};

            // Add plausibility to model nodes
            if (modelToExport && modelToExport.nodes) {
                modelToExport.nodes.forEach(node => {
                    if (node.type === 'model') {
                        // Calculate plausibility from links
                        const links = modelToExport.links || [];
                        node.plausibility = links.reduce((acc, link) => {
                            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                            const targetId = typeof link.target === 'object' ? link.target.id : link.target;

                            // Check if this link connects to this model
                            if (sourceId === node.id || targetId === node.id) {
                                // Skip NO_EFFECT_EXPLICIT (-1) links
                                return acc + (link.value === -1 ? 0 : link.value);
                            }
                            return acc;
                        }, 0);
                    }
                });
            }

            dataToExport = { [currentModel]: modelToExport };
            filename = `${currentModel.toLowerCase().replace(/\s+/g, '_')}_mel_model.json`;
        }

        console.log('Exporting with plausibility:', dataToExport);

        const dataStr = JSON.stringify(dataToExport, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', filename);
        linkElement.click();

        setIsExportModalOpen(false);
    };

    if (!modelData) {
        console.warn('No modelData available');
        return null;
    }

    const modelKeys = Object.keys(modelData);
    if (modelKeys.length === 0) {
        console.warn('modelData is empty');
        return null;
    }
    const Selector = () => (
        <ShimmerHighlight
            isActive={activeShimmer === 'header'}
            style={{
                width: isMobile ? '100%' : 'auto',
                borderRadius: 8
            }}
        >
            <Select
                value={localModel}
                onChange={handleModelChange}
                size="small"
                variant="outlined"
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: isMobile ? '40vh' : '50vh',
                        },
                    },
                }}
                sx={{
                    width: isMobile ? '100%' : 200,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    mb: isMobile ? 1 : 0,
                    '& .MuiSelect-select': {
                        py: 1,
                        fontSize: isMobile ? 16 : 'inherit',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey.300'
                    }
                }}
            >
                {modelKeys.map((key) => (
                    <MenuItem
                        key={key}
                        value={key}
                        sx={{
                            fontSize: isMobile ? 16 : 'inherit',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                        }}
                    >
                        {key}
                    </MenuItem>
                ))}
            </Select>
        </ShimmerHighlight>
    );

    const SaveButton = () => (
        <ShimmerHighlight
            isActive={activeShimmer === 'savechanges'}
            style={{
                width: isMobile ? '100%' : 'auto',
                borderRadius: 8
            }}
        >
            <Button
                onClick={handleSaveStudentChanges}
                disabled={isSaving}
                startIcon={<SaveIcon />}
                size="small"
                sx={{
                    ...buttonStyle,
                    width: isMobile ? '100%' : 'auto',
                    bgcolor: alpha('#1877f2', 0.1),
                    color: 'primary.main',
                    borderRadius: 2,
                    '&:hover': {
                        bgcolor: alpha('#1877f2', 0.2),
                    },
                    '& .MuiButton-startIcon': {
                        color: 'primary.main',
                    },
                    '&.Mui-disabled': {
                        bgcolor: 'grey.100',
                        color: 'grey.500',
                    }
                }}
            >

                {isSaving ? t('header.saving') : t('header.save')}
            </Button>
        </ShimmerHighlight>
    );

    const ExportButton = () => (
        <Button
            onClick={() => setIsExportModalOpen(true)}
            startIcon={<FileDownloadIcon />}
        >
            {t('models.export')}
        </Button>
        );

    const ImportButton = () => (
        <Button
            onClick={handleImportClick}
            startIcon={<FileUploadIcon />}
        >
            {t('models.import')}
        </Button>
    );

    const CreateButton = () => (
        <Button
            onClick={() => setShowAIDialog(true)}
            startIcon={<Brain size={18} />}
            sx={{
                bgcolor: alpha('#1877f2', 0.1),
                color: 'primary.main',
                '&:hover': {
                    bgcolor: alpha('#1877f2', 0.2),
                },
                '& .MuiButton-startIcon': {
                    color: 'primary.main',
                }
            }}
        >
            {t('models.create')}
        </Button>
    );

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            flexDirection: isMobile ? 'column' : 'row',
        }}>
            <Selector />
            <ButtonGroup
                size="small"
                variant="outlined"
                orientation={isMobile ? 'vertical' : 'horizontal'}
                sx={{
                    width: isMobile ? '100%' : 'auto',
                    '& .MuiButton-root': {
                        ...buttonStyle,
                        width: isMobile ? '100%' : 'auto',
                        borderRight: isMobile ? 'none' : '1px solid',
                        borderBottom: isMobile ? '1px solid' : 'none',
                        borderColor: 'grey.300',
                    },
                    '& .MuiButton-root:last-child': {
                        borderRight: 'none',
                        borderBottom: 'none'
                    }
                }}
            >
            {isStudent && (
                <SaveButton />
            )}
            {showAdvancedControls && (
                <span>
                <ExportButton />
                <ImportButton />
                <CreateButton />
                </span>
            )}

            </ButtonGroup>
            <input
                type="file"
                accept=".json"
                onChange={handleImport}
                ref={fileInputRef}
                style={{ display: 'none' }}
            />

            <ExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                onExport={handleExport}
                currentModel={currentModel}
            />

            <MELWizard
                isOpen={showAIDialog}
                onClose={() => setShowAIDialog(false)}
                onMELGenerated={handleImportedData}
            />
        </Box>
    );
};

export default DataModelSelector;